/**
 * 模板市场(屏 2)的人话:字段类型 / 枚举码 / 来源 / 默认值 / 特性码 / 风格族 → 中文;schema 字段计数。
 * 纯函数,零 React。
 * ★ 未知码**原样显示**,不吞、不猜:后端上架一种新特性 / 新枚举,卡上先出英文码也比消失强。
 */
import type { FieldSpec, FieldType, SchemaGroup } from "../api/types";

export const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  text: "文本",
  textarea: "多行文本",
  image: "图片",
  phone: "电话",
  address: "地址",
  enum: "单选",
  list: "列表",
  asset_list: "图片 × N",
  toggle: "开关",
  cta: "按钮",
  news_feed: "自动同步",
};

/**
 * 枚举选项 / 列表默认项的码 → 人话:价格展示四档、按钮动作三档、留资表单三字段(设计稿屏 2 右栏
 * 「隐藏 / 起价 / 区间 / 询价」「姓名 / 手机 / 需求」)。
 */
export const ENUM_OPTION_LABEL: Record<string, string> = {
  hidden: "隐藏",
  starting_from: "起价",
  range: "区间",
  contact: "询价",
  lead_form: "留资表单",
  phone: "打电话",
  wechat: "加微信",
  name: "姓名",
  mobile: "手机",
  need: "需求",
};

export function optionLabel(code: string): string {
  return ENUM_OPTION_LABEL[code] ?? code;
}

/** 模板作者写的预填来源提示(`FieldSpec.source`,不是运行时来源)。 */
export const FIELD_SOURCE_LABEL: Record<string, string> = {
  ai: "AI 起草",
  kb: "来自资料",
  news_cabin: "来自新闻舱,自动同步",
};

export function fieldSourceText(source: string | null | undefined): string | null {
  if (!source) return null;
  return FIELD_SOURCE_LABEL[source] ?? source;
}

/**
 * 默认值的人话:开关 → 「默认开 / 默认关」;列表 → 「姓名 / 手机 / 需求(可增减)」;
 * 枚举 / 文本 → 「默认{选项人话}」。没有默认值回 null(整段不出现)。
 */
export function fieldDefaultText(field: Pick<FieldSpec, "type" | "default">): string | null {
  const v = field.default;
  if (v === undefined || v === null) return null;
  if (field.type === "toggle") return typeof v === "boolean" ? (v ? "默认开" : "默认关") : null;
  if (Array.isArray(v)) {
    const items = v.map((x) => optionLabel(String(x))).filter(Boolean);
    return items.length ? `${items.join(" / ")}(可增减)` : null;
  }
  if (typeof v === "string") return v ? `默认${optionLabel(v)}` : null;
  if (typeof v === "number") return `默认 ${v}`;
  return null;
}

/** 一行的主体:类型人话,带上长度 / 条数 / 枚举选项这些从 schema 现读的约束。 */
function baseText(field: FieldSpec): string {
  const type = FIELD_TYPE_LABEL[field.type] ?? field.type;
  if (field.type === "enum" && field.options?.length) {
    return field.options.map(optionLabel).join(" / ");
  }
  if ((field.type === "text" || field.type === "textarea") && field.maxLen) {
    return `${type} ≤ ${field.maxLen} 字`;
  }
  if (field.type === "list" && field.max) return `${type} ≤ ${field.max} 条`;
  // 企业动态那一行:「自动同步」和「来自新闻舱,自动同步」是一句话,不叠两遍。
  if (field.type === "news_feed" && field.source === "news_cabin") {
    return FIELD_SOURCE_LABEL.news_cabin;
  }
  return type;
}

/**
 * 清单一行右侧那句:「文本 ≤ 40 字 · 必填 · 事实字段」「隐藏 / 起价 / 区间 / 询价 · 事实字段」
 * 「开关 · 默认开」「图片 × N · 事实字段 · 来自资料」。顺序固定:主体 · 必填 · 事实字段 · 需授权公开 ·
 * 默认值 · 来源。电话 / 地址天然是事实字段(只能来自知识库出处或用户手填),模板漏标 fact 也挂上。
 */
export function fieldSpecText(field: FieldSpec): string {
  const parts = [baseText(field)];
  if (field.required) parts.push("必填");
  if (field.fact || field.type === "phone" || field.type === "address") parts.push("事实字段");
  if (field.needsConsent) parts.push("需授权公开");
  const def = fieldDefaultText(field);
  if (def) parts.push(def);
  const src = fieldSourceText(field.source);
  if (src && !(field.type === "news_feed" && field.source === "news_cabin")) parts.push(src);
  return parts.join(" · ");
}

/** spec 级字段总数:repeat 组按 spec 计一次(附录 A.11)。 */
export function schemaFieldCount(schema: ReadonlyArray<Pick<SchemaGroup, "fields">>): number {
  return schema.reduce((n, g) => n + g.fields.length, 0);
}

const FEATURE_LABEL: Record<string, string> = {
  lead_form: "表单",
  ai_chat: "AI 客服",
  map: "地图",
  phone: "电话",
  countdown: "倒计时",
  metrics: "指标",
  spec_table: "参数表",
  credential_wall: "资质墙",
};

export function featureLabel(code: string): string {
  return FEATURE_LABEL[code] ?? code;
}

/** 风格族(TemplateSummary.family)→ 筛选 chip 文案;设计稿屏 2 的那一排。 */
const FAMILY_LABEL: Record<string, string> = {
  corporate: "企业商务",
  tech: "科技未来",
  minimal: "高级极简",
  industry: "工业制造",
  local: "本地门店",
  service: "温暖服务",
  landing: "活动落地页",
};

export function familyLabel(code: string): string {
  return FAMILY_LABEL[code] ?? code;
}
